import type { FeedItem } from '@knocklabs/client';
import { useKnockFeed } from '@knocklabs/react';
import { useEffect, useRef, useState } from 'react';

/**
 * Leitura compartilhada do feed do Knock para a surface de card. Consome o `KnockFeedProvider` único
 * (montado no topo da árvore por `KnockProviders`) — não abre socket próprio. Coleta os ids que chegam
 * ao vivo pelo socket e dispara UM fetch na montagem (o provider só abre o socket; não busca sozinho),
 * para que eventos recentes da página inicial apareçam além das chegadas ao vivo.
 *
 * Type-agnostic: devolve os itens crus + os ids realtime; cada tipo filtra por workflow key + shape no
 * seu próprio `parse` e aplica sua própria janela de recência.
 */
export const useNotificationFeed = () => {
  const { feedClient, useFeedStore } = useKnockFeed();
  const feedItems = useFeedStore((state) => state.items);

  const [realtimeItemIds, setRealtimeItemIds] = useState<ReadonlySet<string>>(() => new Set());

  useEffect(() => {
    const collect = ({ items }: { items: Array<{ id: string }> }) => {
      setRealtimeItemIds((prev) => {
        const next = new Set(prev);
        for (const { id } of items) next.add(id);
        return next;
      });
    };

    feedClient.on('items.received.realtime', collect);
    return () => feedClient.off('items.received.realtime', collect);
  }, [feedClient]);

  useEffect(() => {
    void feedClient.fetch();
  }, [feedClient]);

  return { feedClient, feedItems, realtimeItemIds };
};

/**
 * Assina apenas os pushes ao vivo do socket (não o fetch da montagem) e chama `handler` para cada lote.
 * É o que a surface de toast usa: só notifica eventos genuinamente novos, nunca histórico ao trocar de página.
 */
export const useRealtimeFeedItems = (handler: (items: FeedItem[]) => void) => {
  const { feedClient } = useKnockFeed();

  // Ref guarda a última versão do handler — assim o listener não é re-registrado a cada render quando o
  // caller passa um handler não-memoizado (deps do effect = só `feedClient`).
  const handlerRef = useRef(handler);
  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  useEffect(() => {
    const onRealtime = ({ items }: { items: FeedItem[] }) => handlerRef.current(items);
    feedClient.on('items.received.realtime', onRealtime);
    return () => feedClient.off('items.received.realtime', onRealtime);
  }, [feedClient]);
};
